<template>
  <div>
    <div class="navigation-bar">
      <span @click="back" class="back-button">
        <font-awesome-icon icon="fa-solid fa-arrow-left"></font-awesome-icon>
      </span>
      <div class="d-flex flex-row">
        <div class="d-flex flex-column">
          <span class="balance-label">Balance Due:</span>
          <h3 class="balance-amount"> {{ $utils.formatMoney(saleTotal - discount.total) }}</h3>
        </div>
      </div>
    </div>

    <custom-modal :is-form="true" ref="lpoModal" @submit="setLPO" title="Select LPO Customer">
      <div class="form-group mb-3">
        <label for="lpo_debtor" class="form-label">Customer</label>
        <v-select id="lpo_debtor"
                  v-model="lpoDetails.debtorID"
                  :options="$store.getters.getDebtors"
                  :reduce="debtor=>debtor.Id"
                  label="Name"
                  :required="!lpoDetails.debtorID"
                  @search="searchDebtors"
        ></v-select>
      </div>

      <div class="form-group mb-3">
        <label for="lpo_number" class="form-label">
          LPO NO.
          <input class="form-control" required type="text" minlength="10" maxlength="10" v-model="lpoDetails.lpoNumber" id="lpo_number">
        </label>
      </div>
    </custom-modal>

    <div class="payment-body">
      <!-- Main Payment Section -->
      <div class="main-payment-section">
        <!-- Amount Input and Keypad Section -->
        <div class="amount-keypad-section">
          <div class="amount-input-container" v-if="updateCardTotal">
            <label class="amount-input-label">
              <b>Amount Paid</b>
              <input class="input-amount"
                     oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '0');"
                     pattern="[+-]?([0-9]*[.])?[0-9]+" type="text" v-model="paidAmount">
            </label>
          </div>
          
          <!-- Keypad -->
          <div class="keypad-container" v-if="updateCardTotal">
            <div class="keypad-grid">
              <button @click="inputNumber($event)" class="number-btn" type="button">1</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">2</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">3</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">4</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">5</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">6</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">7</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">8</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">9</button>
              <button @click="addPoint" class="number-btn function-btn" type="button">.</button>
              <button @click="inputNumber($event)" class="number-btn" type="button">0</button>
              <button @click="backspace" class="number-btn function-btn" type="button">
                <font-awesome-icon icon="fa-solid fa-delete-left"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Payment Methods Section -->
        <div class="payment-methods-section">
          <DesktopPaymentMethod :minimum-amount="paidAmount" @processPayment="processTransaction" />
          
          <!-- Tip Badge -->
          <div class="tip-badge-container" v-if="tipAmount > 0">
            <span class="badge bg-info">Tip: {{ $utils.formatMoney(tipAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- Bottom Section: Purchase Order, Discount, and Reference -->
      <div class="bottom-section">
        <!-- Purchase Order and Discount Row -->
        <div class="order-discount-row">
          <!-- LPO Section -->
          <div class="lpo-section">
            <h6>VAT Exemption LPO</h6>
            <div class="lpo-content">
              <a class="btn lpo-btn" :class="{ disabled: !isOnline }" @click="openLPO" v-if="!storeLPONumber && isOnline">Add LPO</a>
              <a class="btn lpo-btn" :class="{ disabled: !isOnline }" v-else-if="!storeLPONumber && !isOnline" style="opacity: 0.6; cursor: not-allowed;">Add LPO (Offline)</a>
              <a class="btn lpo-btn lpo-remove-btn" :class="{ disabled: !isOnline }" @click="removeLPO" v-else-if="storeLPONumber && isOnline">Remove LPO</a>
              <a class="btn lpo-btn lpo-remove-btn" :class="{ disabled: !isOnline }" v-else-if="storeLPONumber && !isOnline" style="opacity: 0.6; cursor: not-allowed;">Remove LPO (Offline)</a>
            </div>
          </div>

          <!-- Discount Section -->
          <div class="discount-section">
            <h6>Apply Discount (Optional)</h6>
            <div class="discount-box" @click="openDiscount">
              <div class="discount-content">
                <p v-if="!discountApplied">Tap to apply discount</p>
                <p v-else>{{ discountText }}</p>
              </div>
              <div class="discount-actions">
                <a v-if="discountApplied" class="icon-btn text-danger" @click.stop="clearDiscount">
                  <font-awesome-icon icon="fa-solid fa-xmark"/>
                </a>
                <a class="icon-btn">
                  <font-awesome-icon icon="fa-solid fa-chevron-right"/>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Reference Section -->
        <div class="reference-section">
          <label for="saleReference" class="form-label">Purchase Order / Reference / Note(s)</label>
          <textarea class="form-control" placeholder="Purchase Order / Reference / Note(s)" rows="2"
                    required id="saleReference" v-model="reference"/>
        </div>
      </div>

    </div>

    <DiscountSheet ref="discount"/>
  </div>
</template>

<script>
import DesktopPaymentMethod from "@/components/point-of-sale/sales/payment/DesktopPaymentMethod";
import DiscountSheet from "@/components/point-of-sale/sales/Discount/DiscountSheet";
import CurrentSaleTable from "@/components/point-of-sale/sales/CurrentSaleTable";
import ViewItems from "@/components/point-of-sale/sales/payment/ViewItems";
import SaleDetails from "@/components/point-of-sale/sales/SaleDetails";
import CustomModal from "@/components/layout/utils/CustomModal";
import * as responses from "@/utils/responses";
import Swal from 'sweetalert2';
import { Storage } from "@/services/Storage";
import connectivityMonitor from '@/services/connectivityMonitor';

export default {
  name: "PaymentScreen",
  components: {
    DesktopPaymentMethod,
    CustomModal,
    SaleDetails,
    ViewItems,
    CurrentSaleTable,
    DiscountSheet,
  },
  computed: {
    saleUnits() {
      return this.$store.getters.getSaleUnits;
    },
    tipAmount() {
      return this.$store.getters.getTipAmount;
    },
    discountApplied() {
      return this.discount.type !== 'none';
    },
    saleTotal() {
      return this.$store.getters.getSaleTotal + this.$store.getters.getTipAmount;
    },
    discountText() {
      let discount = this.discount;
      return `${discount.type} (${discount.type === "Percentage" ? `${discount.value}%` : discount.value})`;
    },
    discount() {
      return this.$store.getters.getSaleDiscount;
    },
    change() {
      let val = this.paidAmount - Number((this.saleTotal - this.discount.total))
      return val < 0 ? 0 : val.toFixed(2);
    },
    saleDetails() {
      return this.$store.getters.getSaleDetails
    },
    currentSaleLines() {
      return this.$store.getters.getSaleLines
    },
    storeLPONumber() {
      return this.$store.getters.getLPO;
    },
    updateCardTotal() {
      return this.$store.dispatch(`validateSecurityPermissionNoPrompt`,`updateCardTotal`);
    },
  },
  data() {
    return {
      paidAmount: "",
      paymentType: "Cash",
      loading: false,
      reference: "",
      isOnline: true,
      unsubscribeOnline: null,
      unsubscribeOffline: null,
      lpoDetails: {
        debtorID: null,
        lpoNumber: null,
      },
    };
  },
  mounted() {
    // Initialize online status
    this.isOnline = connectivityMonitor.getStatus();

    // Subscribe to connectivity events
    this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
      this.isOnline = true;
    });

    this.unsubscribeOffline = connectivityMonitor.subscribe('offline', () => {
      this.isOnline = false;
    });
  },
  beforeDestroy() {
    // Unsubscribe from connectivity events
    if (this.unsubscribeOnline) {
      this.unsubscribeOnline();
    }
    if (this.unsubscribeOffline) {
      this.unsubscribeOffline();
    }
  },
  beforeMount() {
    // Reset stale refund flag so Kazang/Lay-By buttons can't be hidden
    // by an abandoned refund flow (PaymentView's `setSaleDetails` may
    // still hold `refund: true` from RefundInvoice).
    this.$store.commit('setSaleDetails', {});
  },
  methods: {
    clearDiscount() {
      return this.$store.commit("clearDiscount");
    },

    async openDiscount() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `Discount`)
      if (!isAllowed) return;
      if (this.$refs.discount && typeof this.$refs.discount.open === 'function') {
        this.$refs.discount.open();
      } else {
        console.error("Discount ref is undefined or does not have an 'open' method");
      }
    },
    checkPaidAmount() {
      if (this.paidAmount === "") {
        let amount = parseFloat(this.saleTotal) - parseFloat(this.discount.total);
        return amount < 0 ? 0 : amount;
      }
      return this.paidAmount;
    },
    updatePaidAmount(value) {
      this.paidAmount = parseFloat(value).toFixed(2);
    },
    backspace() {
      this.paidAmount = this.paidAmount.slice(0, -1);
    },
    addPoint() {
      if (!this.paidAmount.includes(".")) this.paidAmount += ".";
    },
    async checkOnlineStorePaymentStatus(saleNum) {
      try {
        const store = await Storage.getDefaultStore();
        const storeDB = store.StoreDB;
        const storeOrdersService = (await import('@/services/storeOrdersService')).default;
        const response = await storeOrdersService.getSale(storeDB, saleNum);

        if (response.status === 'SUCCESS' && response.data && response.data.payment) {
          return {
            isPaid: response.data.payment.status === 'PAID',
            paymentMethod: response.data.payment.method,
            paymentReference: response.data.payment.paymentReference,
            orderData: response.data
          };
        }
        return { isPaid: false };
      } catch (error) {
        console.error('Error checking online store payment status:', error);
        return { isPaid: false, error: true };
      }
    },
    async openCashDrawer() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `OpenDrwer`);
      if (isAllowed) return this.$store.dispatch(`openCashDrawer`)
    },
    back() {
      this.$store.dispatch(`calculateTipAmount`);
      this.$store.commit('clearDiscount');
      return this.$router.push({
        name: `pos`,
        query: {
          saleNumber: this.$store.getters.getCurrentSale,
          dontReload: true
        }
      });
    },
    processTransaction: async function (type) {
      if (this.currentSaleLines.length === 0) {
        return responses.showWarning('You need to add Items to Sale!');
      }
      const paid = parseFloat(this.checkPaidAmount());
      const balance = parseFloat(this.saleTotal) - parseFloat(this.discount.total);
      if (paid < balance) {
        return responses.showWarning('Amount paid is less than balance due');
      }

      this.$utils.showLoading();

      // Check if this is an online store table (table name starts with shop.co.zm)
      const openTableName = this.$store.getters.getOpenTableName;
      const currentSale = this.$store.getters.getCurrentSale;

      if (openTableName && openTableName.startsWith('shop.co.zm') && currentSale) {
        try {
          const paymentStatus = await this.checkOnlineStorePaymentStatus(currentSale);

          if (paymentStatus.isPaid && paymentStatus.paymentMethod &&
              paymentStatus.paymentMethod.includes('Kazang')) {
            // Order is already paid via Kazang, process as Kazang payment
            this.$utils.hideLoading();
            return this.$store.dispatch('processKazangInvoice', {
              amount: parseFloat(this.saleTotal.toFixed(2)),
              SecondRefrence: paymentStatus.paymentReference
            }).then(() => {
              return this.$router.push({ name: 'viewPayment' });
            });
          }
        } catch (error) {
          console.error('Error processing online store payment:', error);
        }
      }

      // Normal payment processing
      let obj = this.saleDetails;
      obj.type = type;
      this.openCashDrawer();
      return this.$store.dispatch("processTransaction", {
        ...obj,
        refund: false,
        reference: this.reference,
        total: parseFloat(this.saleTotal.toFixed(2)),
        paid: this.checkPaidAmount(),
        change: parseFloat(Number(this.change).toFixed(2))
      })
          .then((res) => {
            if (res) {
              if (!androidMode) {
              }
            }
          }).finally(() => {
            this.$utils.hideLoading();
          });
    },
    inputNumber(event) {
      let elem = event.target
      if (elem) {
        let txt = elem.textContent || elem.innerText;
        if (txt) {
          if (this.paidAmount === '0') {
            this.paidAmount = ''
          }
          this.paidAmount += txt
        }
      }
    },
    setLPO() {
      if (!this.lpoDetails.debtorID) {
        responses.showWarning(`Please Select a Customer`)
        return this.$refs.lpoModal.openModal()
      }
      this.$store.commit(`setLPO`, this.lpoDetails);
      this.$store.dispatch(`setCustomer`, this.lpoDetails.debtorID);
      this.setAmount(); // Auto-fill paidAmount with sale total for LPO
      return responses.showSuccess(`LPO set successfully`);
    },
    removeLPO() {
      return Swal.fire({
        icon: `question`,
        title: `Do you want also want to remove customer from sale `,
        showCancelButton: true,
        confirmButtonText: `Sure`,
        cancelButtonText: `No , only remove LPO from sale`,
      }).then(res => {
        if (res.isConfirmed) {
          this.$store.dispatch(`setCustomer`, ``)
          this.lpoDetails.debtorID = ``
        }
        this.$store.commit(`setLPO`, {
          debtorID: this.lpoDetails.debtorID,
          lpoNumber: undefined,
        });
        this.lpoDetails.lpoNumber = ``
        return responses.showInfo(`LPO removed successfully`)
      })
    },
    openLPO() {
      this.$refs.lpoModal.openModal();
      let obj = this.lpoDetails;
      this.lpoDetails = {
        debtorID: obj.debtorID ? obj.debtorID : this.$store.getters.getCustomer,
        lpoNumber: obj.lpoNumber ? obj.lpoNumber : this.$store.getters.getLPO,
      }
    },
    searchDebtors(input, loading) {
      loading(true);
      return this.$store.dispatch(`getAllDebtors`, {
        Name: input,
        tpin: "",
        contact: ""
      }).finally(() => loading(false))
    },
    setAmount() {
      if (!this.paidAmount) {
        this.paidAmount = Number(this.saleTotal - this.discount.total).toFixed(2)
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.navigation-bar {
  width: 100%;
  background: var(--card-bg);
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 25px;
  padding-left: 25px;
  border-bottom: 1px solid var(--card-border);
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  box-shadow: var(--card-shadow-sm);

  @media (max-width: 768px) {
    height: 60px;
    padding-left: 15px;
    gap: 15px;
  }

  .back-button {
    font-size: 28px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: var(--transition-speed);

    &:hover {
      color: var(--accent-electric-blue);
    }

    @media (max-width: 768px) {
      font-size: 22px;
    }
  }

  .balance-label {
    color: var(--text-secondary);
    font-size: 0.95rem;
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }

  .balance-amount {
    font-family: var(--font-family);
    font-weight: 700;
    color: var(--text-primary);
    font-size: 1.6rem;
    margin: 0;

    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
}

.payment-body {
  background: var(--bg-gradient);
  min-height: calc(100vh - 70px);
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 15px;
  font-family: var(--font-family);
  color: var(--text-primary);

  @media (max-width: 768px) {
    min-height: calc(100vh - 60px);
    padding: 10px;
    padding-bottom: 80px;
  }
}

.main-payment-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  background: var(--card-bg);
  border-radius: 15px;
  box-shadow: var(--card-shadow);
  padding: 20px;
  height: fit-content;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
}

.amount-keypad-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.amount-input-container {
  .amount-input-label {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 1rem;

    b {
      display: block;
      text-align: center;
      margin-bottom: 8px;
      color: var(--text-primary);
      font-size: 1.1rem;
    }
  }

  .input-amount {
    border: none;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-primary);
    text-align: center;
    width: 100%;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--card-border);
    background-color: transparent;
    transition: var(--transition-speed);

    &:focus, &:active {
      border-bottom: 3px solid var(--accent-electric-blue) !important;
      outline: none;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
}

.keypad-container {
  width: 70%;
  margin-left: 15%;
  height: 100%;
  align-items: center;
  justify-content: center;
  .keypad-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;

    .number-btn {
      background: var(--bg-secondary);
      border-radius: 10px;
      border: 1px solid #2c2a2a;
      height: 50px;
      margin-bottom: 20px;
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--text-primary);
      transition: var(--transition-speed);
      box-shadow: var(--card-shadow-sm);

      &:hover {
        background: var(--bg-primary-dark);
        box-shadow: var(--card-shadow-hover);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      &.function-btn {
        background: var(--accent-electric-blue);
        color: white;
        font-size: 1.3rem;

        &:hover {
          background: var(--accent-electric-blue-dark);
        }
      }

      svg {
        font-size: 1.2rem;
      }

      @media (max-width: 768px) {
        height: 45px;
        font-size: 1.3rem;
      }
    }
  }
}

.payment-methods-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.tip-badge-container {
  display: flex;
  justify-content: flex-end;

  .badge {
    background-color: var(--info-color) !important;
    color: white;
    font-size: 0.95rem;
    padding: 6px 10px;
    border-radius: 8px;
  }
}

.bottom-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.order-discount-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

.lpo-section, .discount-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 15px;

  h6 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 10px 0;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
}

.lpo-content {
  .lpo-btn {
    background: var(--secondary-button-bg);
    color: var(--text-primary);
    border: 1px solid var(--card-border);
    border-radius: 8px;
    padding: 8px 15px;
    font-size: 0.85rem;
    font-weight: 600;
    transition: var(--transition-speed);
    box-shadow: var(--card-shadow-sm);
    width: 100%;
    text-align: center;

    &:hover {
      background: var(--secondary-button-hover-bg);
      box-shadow: var(--card-shadow-hover);
    }

    &.lpo-remove-btn {
      background: var(--danger);
      color: white;
      border-color: var(--danger);

      &:hover {
        background: var(--danger-dark);
        border-color: var(--danger-dark);
      }
    }

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
      background: var(--disabled-button-bg) !important;
      color: var(--disabled-button-text) !important;
      border-color: var(--disabled-button-bg) !important;

      &:hover {
        background: var(--disabled-button-bg) !important;
        box-shadow: var(--card-shadow-sm);
      }
    }
  }
}

.discount-box {
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px 12px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition-speed);
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 40px;

  &:hover {
    border-color: var(--accent-electric-blue);
  }

  .discount-content {
    p {
      margin: 0;
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--text-primary);
    }
  }

  .discount-actions {
    display: flex;
    gap: 8px;

    .icon-btn {
      color: var(--text-secondary);
      font-size: 1rem;
      cursor: pointer;
      transition: var(--transition-speed);

      &:hover {
        color: var(--accent-electric-blue);
      }

      &.text-danger {
        color: var(--danger) !important;

        &:hover {
          color: var(--danger-dark) !important;
        }
      }
    }
  }
}

.reference-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 15px;

  @media (max-width: 768px) {
    padding: 12px;
  }

  label {
    margin-bottom: 8px;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 0.95rem;
  }

  textarea {
    font-size: 0.9rem;
    padding: 10px 12px;
    border: 1px solid var(--card-border);
    border-radius: 8px;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    transition: var(--transition-speed);
    resize: vertical;

    &:focus {
      border-color: var(--accent-electric-blue);
      box-shadow: 0 0 0 2px rgba(var(--accent-electric-blue-rgb), 0.15);
      outline: none;
    }

    &::placeholder {
      color: var(--text-secondary);
      opacity: 0.7;
    }
  }
}

@media (max-width: 576px) {
  .main-payment-section {
    padding: 12px;
  }
  
  .order-discount-row {
    gap: 8px;
  }
  
  .lpo-section, .discount-section, .reference-section {
    padding: 10px;
  }
}
</style>
